// ============================================
// Fallback Validation Service
// Two validation modes:
//   1. Full validation (validateFallbackModel):
//      Runs all critical tests for the agent type.
//      Returns APPROVED / REJECTED / MANUAL_REVIEW.
//   2. Emergency validation (emergencyValidation):
//      Abbreviated test set, conservative logic.
//      Returns APPROVED / REJECTED only.
//
// Deterministic — no LLM dependency.
//
// Conservative policy:
//   - ANY critical test failure → REJECTED
//   - ALL critical tests pass → APPROVED
//   - Full validation: >0 failures but <50% → MANUAL_REVIEW
// ============================================

import type { AgentId } from './firestore/agentBrandService';
import {
  EMERGENCY_CRITICAL_TEST_IDS,
  type EmergencyTestDef,
} from '../config/emergencyTestSelection';

// ── Pre-validation & Safety Test Types ──

/**
 * Supported input formats per agent type.
 * Used by preValidateModel to verify a model can accept the
 * expected input shape for its agent role.
 */
export type ModelInputFormat = 'lab_panel_json' | 'conversation_turn' | 'formulary_request' | 'biomarker_narrative';

const AGENT_INPUT_FORMATS: Record<AgentId, ModelInputFormat> = {
  captain: 'lab_panel_json',
  nurse: 'conversation_turn',
  protocol_builder: 'formulary_request',
  report_generator: 'biomarker_narrative',
};

/**
 * A model candidate to be validated before deployment.
 */
export interface ModelCandidate {
  /** Model identifier (e.g. 'medgemma-27b-v2') */
  modelId: string;
  /** Input formats this model supports */
  supportedInputFormats: ModelInputFormat[];
  /** Function that processes an input string and returns an output string */
  invoke: (input: string) => Promise<string>;
}

/**
 * A single test case in a safety validation suite.
 */
export interface ValidationTestCase {
  /** Unique test identifier (e.g. 'CAP-E01') */
  id: string;
  /** Human-readable description */
  label: string;
  /** The input to send to the model */
  input: string;
  /** Function that evaluates whether the model output passes */
  validate: (output: string) => boolean;
}

/**
 * Result of executing a single ValidationTestCase.
 * Records id, input, actualOutcome, and pass/fail status (c3).
 */
export interface ValidationTestResult {
  /** Test case ID */
  id: string;
  /** The input that was sent to the model */
  input: string;
  /** The actual output returned by the model */
  actualOutcome: string;
  /** Whether the test passed or failed */
  passed: boolean;
}

/**
 * A suite of validation test cases to run against a model.
 */
export interface ValidationTestSuite {
  /** Suite identifier */
  suiteId: string;
  /** Agent type this suite validates */
  agentType: AgentId;
  /** The test cases in this suite */
  testCases: ValidationTestCase[];
}

/**
 * Aggregated results from executing a full safety test suite.
 */
export interface SafetyTestExecutionResult {
  suiteId: string;
  modelId: string;
  results: ValidationTestResult[];
  allPassed: boolean;
  passedCount: number;
  failedCount: number;
}

// ── Pre-Validation & Safety Test Execution ──

/**
 * Verify that a model candidate supports the input format
 * required by the given agent type. Returns true if the model
 * can accept the agent's expected input format, false otherwise.
 *
 * @param model - The model candidate to validate
 * @param agentType - The agent role the model will serve
 * @returns true if model supports the required input format
 * @throws If agentType is not recognized
 */
export function preValidateModel(model: ModelCandidate, agentType: AgentId): boolean {
  const requiredFormat = AGENT_INPUT_FORMATS[agentType];
  if (!requiredFormat) {
    throw new Error(
      `[fallbackValidationService] Unknown agentType for pre-validation: ${agentType}. ` +
      `Valid types: ${Object.keys(AGENT_INPUT_FORMATS).join(', ')}`
    );
  }

  if (!model || !model.modelId || !Array.isArray(model.supportedInputFormats)) {
    return false;
  }

  return model.supportedInputFormats.includes(requiredFormat);
}

/**
 * Execute all test cases in a safety test suite against a model.
 * Each test is invoked, validated, and its result recorded with
 * id, input, actualOutcome, and pass/fail status.
 *
 * @param model - The model candidate to test
 * @param testSuite - The validation test suite to execute
 * @returns Aggregated execution results
 */
export async function executeSafetyTests(
  model: ModelCandidate,
  testSuite: ValidationTestSuite
): Promise<SafetyTestExecutionResult> {
  if (!testSuite.testCases || testSuite.testCases.length === 0) {
    throw new Error(
      '[fallbackValidationService] Cannot execute empty test suite. ' +
      'At least one ValidationTestCase is required.'
    );
  }

  const results: ValidationTestResult[] = [];

  for (const testCase of testSuite.testCases) {
    let actualOutcome: string;
    let passed: boolean;

    try {
      actualOutcome = await model.invoke(testCase.input);
      passed = testCase.validate(actualOutcome);
    } catch (err) {
      // Model invocation failure = automatic fail
      actualOutcome = err instanceof Error ? err.message : String(err);
      passed = false;
    }

    results.push({
      id: testCase.id,
      input: testCase.input,
      actualOutcome,
      passed,
    });
  }

  const passedCount = results.filter((r) => r.passed).length;
  const failedCount = results.length - passedCount;

  return {
    suiteId: testSuite.suiteId,
    modelId: model.modelId,
    results,
    allPassed: failedCount === 0,
    passedCount,
    failedCount,
  };
}

/**
 * Result of a single emergency test execution.
 */
export interface EmergencyTestResult {
  /** Test ID (e.g. 'CAP-E01') */
  testId: string;
  /** Whether this test passed */
  passed: boolean;
  /** Optional detail on failure reason */
  failureReason?: string;
}

/**
 * Emergency threshold evaluation verdict.
 * Conservative: any failure = REJECTED.
 */
export type EmergencyVerdict = 'APPROVED' | 'REJECTED';

/**
 * Full validation verdict.
 * MANUAL_REVIEW: some non-critical failures detected — needs human review.
 */
export type ValidationVerdict = 'APPROVED' | 'REJECTED' | 'MANUAL_REVIEW';

/**
 * Full evaluation output with verdict + details.
 */
export interface EmergencyEvaluationResult {
  verdict: EmergencyVerdict;
  totalTests: number;
  passedCount: number;
  failedCount: number;
  failures: Array<{
    testId: string;
    label: string;
    failureReason?: string;
  }>;
}

/**
 * Select the abbreviated set of CRITICAL-level tests
 * for a given agent type. Returns the top 10 predefined
 * emergency tests for that agent.
 *
 * @param agentType - The agent whose tests to retrieve
 * @returns Array of up to 10 CRITICAL emergency test definitions
 * @throws If agentType is not recognized
 */
export function selectAbbreviatedTests(agentType: AgentId): EmergencyTestDef[] {
  const tests = EMERGENCY_CRITICAL_TEST_IDS[agentType];
  if (!tests) {
    throw new Error(
      `[fallbackValidationService] Unknown agentType: ${agentType}. ` +
      `Valid types: ${Object.keys(EMERGENCY_CRITICAL_TEST_IDS).join(', ')}`
    );
  }
  // Return top 10 CRITICAL-level tests (all are CRITICAL by definition)
  return tests.slice(0, 10);
}

/**
 * Evaluate emergency thresholds against test results.
 * Conservative approval logic:
 *   - Returns 'REJECTED' if ANY critical test fails
 *   - Returns 'APPROVED' only if ALL critical tests pass
 *
 * @param testResults - Array of test execution results
 * @returns Evaluation result with verdict and failure details
 * @throws If testResults is empty (cannot approve with no evidence)
 */
export function evaluateEmergencyThresholds(
  testResults: EmergencyTestResult[]
): EmergencyEvaluationResult {
  if (!testResults || testResults.length === 0) {
    throw new Error(
      '[fallbackValidationService] Cannot evaluate empty test results. ' +
      'At least one test result is required.'
    );
  }

  // Resolve test labels from the config for failure reporting
  const allTestDefs = Object.values(EMERGENCY_CRITICAL_TEST_IDS).flat();
  const testDefMap = new Map(allTestDefs.map((t) => [t.id, t]));

  const failures: EmergencyEvaluationResult['failures'] = [];

  for (const result of testResults) {
    if (!result.passed) {
      const def = testDefMap.get(result.testId);
      failures.push({
        testId: result.testId,
        label: def?.label ?? 'Unknown test',
        failureReason: result.failureReason,
      });
    }
  }

  const passedCount = testResults.length - failures.length;

  // Conservative: any failure → REJECTED
  const verdict: EmergencyVerdict = failures.length > 0 ? 'REJECTED' : 'APPROVED';

  return {
    verdict,
    totalTests: testResults.length,
    passedCount,
    failedCount: failures.length,
    failures,
  };
}

// ── Full Validation Result ──

export interface FullValidationResult {
  verdict: ValidationVerdict;
  totalTests: number;
  passedCount: number;
  failedCount: number;
  failures: Array<{
    testId: string;
    label: string;
    failureReason?: string;
  }>;
}

/**
 * Run the full critical test suite for an agent and evaluate
 * with three-tier verdict logic:
 *   - ALL pass → APPROVED
 *   - ≥50% fail → REJECTED
 *   - >0 but <50% fail → MANUAL_REVIEW
 *
 * This is the non-emergency path — used when no active patient
 * session is in flight and we have time for thorough validation.
 */
export function validateFallbackModel(
  agentType: AgentId,
  testResults: EmergencyTestResult[],
): FullValidationResult {
  const tests = EMERGENCY_CRITICAL_TEST_IDS[agentType];
  if (!tests) {
    throw new Error(
      `[fallbackValidationService] Unknown agentType: ${agentType}. ` +
      `Valid types: ${Object.keys(EMERGENCY_CRITICAL_TEST_IDS).join(', ')}`
    );
  }

  if (!testResults || testResults.length === 0) {
    throw new Error(
      '[fallbackValidationService] Cannot validate with empty test results.'
    );
  }

  const allTestDefs = Object.values(EMERGENCY_CRITICAL_TEST_IDS).flat();
  const testDefMap = new Map(allTestDefs.map((t) => [t.id, t]));

  const failures: FullValidationResult['failures'] = [];
  for (const result of testResults) {
    if (!result.passed) {
      const def = testDefMap.get(result.testId);
      failures.push({
        testId: result.testId,
        label: def?.label ?? 'Unknown test',
        failureReason: result.failureReason,
      });
    }
  }

  const passedCount = testResults.length - failures.length;
  const failRate = failures.length / testResults.length;

  let verdict: ValidationVerdict;
  if (failures.length === 0) {
    verdict = 'APPROVED';
  } else if (failRate >= 0.5) {
    verdict = 'REJECTED';
  } else {
    verdict = 'MANUAL_REVIEW';
  }

  return {
    verdict,
    totalTests: testResults.length,
    passedCount,
    failedCount: failures.length,
    failures,
  };
}

/**
 * Emergency validation wrapper: abbreviated test selection +
 * conservative two-tier evaluation (APPROVED/REJECTED only).
 * Used when a patient session is active and speed matters.
 */
export function emergencyValidation(
  agentType: AgentId,
  testResults: EmergencyTestResult[],
): EmergencyEvaluationResult {
  // Use abbreviated test set — validates only the tests relevant to this agent (pre-computed, unused in this path)
  void selectAbbreviatedTests(agentType);
  // Evaluate with conservative logic (any failure → REJECTED)
  return evaluateEmergencyThresholds(testResults);
}

// ── Implementation History (Reference Only) ──
//
// The following directives were executed during early exploratory development
// under the old C-series numbering (pre-bone migration). This spec remains in
// PLAN stage. These artifacts represent preliminary work, not production-ready
// implementation. Spec validation and certification are independent of
// implementation history.
//
// Compliance gate: None of this code is considered validated. Before any of it
// can be used in a BUILD phase, it must pass a full audit against this spec's
// current requirements. Code that fails the audit must be rewritten or discarded.

/**
 * Validation status for historical implementation artifacts.
 * All pre-bone-migration code starts as NOT_VALIDATED.
 */
export type HistoricalValidationStatus = 'NOT_VALIDATED' | 'AUDIT_PASSED' | 'AUDIT_FAILED' | 'DISCARDED';

/**
 * A single entry in the implementation history registry.
 * Tracks directives executed during early C-series exploratory development.
 */
export interface ImplementationHistoryEntry {
  /** ISO date when the directive was executed */
  date: string;
  /** Directive ID (old C-series identifier) */
  directiveId: string;
  /** Human-readable description of what the directive built */
  description: string;
  /** Current validation status — all start NOT_VALIDATED per compliance gate */
  validationStatus: HistoricalValidationStatus;
}

/**
 * Implementation history registry for CAP.18 (Fallback Validation).
 *
 * These directives were executed under the old C-series numbering before
 * the bone migration. None are considered validated. Before any artifact
 * can be used in a BUILD phase, it must pass a full audit against the
 * current spec requirements. Code that fails the audit must be rewritten
 * or discarded.
 */
export const CAP18_IMPLEMENTATION_HISTORY: readonly ImplementationHistoryEntry[] = [
  { date: '2026-03-10', directiveId: 'Nsee2jXs', description: 'Emergency Validation Mode', validationStatus: 'NOT_VALIDATED' },
  { date: '2026-03-10', directiveId: 'o0bMhgYc', description: 'Agent-Specific Conversational Test Cases', validationStatus: 'NOT_VALIDATED' },
  { date: '2026-03-10', directiveId: 'tcaD8EMC', description: 'Agent-Specific Test Cases (Nurse)', validationStatus: 'NOT_VALIDATED' },
  { date: '2026-03-10', directiveId: 'vb8SaJkw', description: 'Agent-Specific Test Cases (Protocol Builder)', validationStatus: 'NOT_VALIDATED' },
  { date: '2026-03-10', directiveId: 'Y2mfSFjB', description: 'Emergency Validation Mode', validationStatus: 'NOT_VALIDATED' },
  { date: '2026-03-10', directiveId: 'Axm4fzuL', description: 'Agent-Specific Test Cases (Report Generator)', validationStatus: 'NOT_VALIDATED' },
  { date: '2026-03-10', directiveId: 'ZQwwqFsn', description: 'Agent-Specific Test Cases (Captain)', validationStatus: 'NOT_VALIDATED' },
  { date: '2026-03-10', directiveId: '2bbfasFo', description: 'Integration with Provider Router Failover Logic', validationStatus: 'NOT_VALIDATED' },
  { date: '2026-03-10', directiveId: '2gUdmW3b', description: 'Validation Process - Pre-Validation & Safety Test Execution', validationStatus: 'NOT_VALIDATED' },
  { date: '2026-03-09', directiveId: '0wHJbINc', description: 'Emergency Validation Mode - Abbreviated Test Selection', validationStatus: 'NOT_VALIDATED' },
] as const;

/**
 * Check whether a historical directive has passed audit and can be
 * used in a BUILD phase. Returns false for all NOT_VALIDATED entries.
 */
export function isHistoricalDirectiveValidated(directiveId: string): boolean {
  const entry = CAP18_IMPLEMENTATION_HISTORY.find(e => e.directiveId === directiveId);
  if (!entry) {
    return false;
  }
  return entry.validationStatus === 'AUDIT_PASSED';
}
